import { useMutation } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { WritableAccessMetadata } from '@opallabs/concepts'
import { computeDurationFromDates } from '@opallabs/lambda'
import { OpalApiRequestBody, OpalApiResponseDocument } from '../../../schema'
import { blocks } from '../../../v3/blocks'

export type UpdateBlockMutationParams = {
  access?: WritableAccessMetadata
  categoryType?: { id: string; name: string } | null
  endDate?: Date
  id: string
  startDate?: Date
  title?: string
}

type BlockResponseDocument = OpalApiResponseDocument<'UpdateBlockV3', 200>
type BlockRequestBody = OpalApiRequestBody<'UpdateBlockV3'>

type QueryParams = {
  onMutate?: (params: UpdateBlockMutationParams) => void
  onSuccess?: (
    data: BlockResponseDocument,
    params: UpdateBlockMutationParams
  ) => void
}

const buildRequestBody = ({
  id,
  categoryType,
  startDate,
  endDate,
  title,
  access,
}: UpdateBlockMutationParams): BlockRequestBody => ({
  data: {
    id: id,
    type: 'block',
    attributes: {
      ...(title && { title }),
      start_at: startDate ? formatISO(startDate) : undefined,
      duration:
        endDate && startDate
          ? computeDurationFromDates(startDate, endDate)
          : undefined,
    },
    ...(access !== undefined && { meta: { access } }),
    relationships: {
      category_type:
        categoryType !== undefined
          ? {
              data: categoryType
                ? { id: categoryType.id, type: 'category_type' }
                : null,
            }
          : undefined,
    },
  },
})

export const useUpdateBlock = ({ onMutate, onSuccess }: QueryParams) => {
  return useMutation({
    mutationFn: (params: UpdateBlockMutationParams) => {
      return blocks.update({
        params: {
          path: { block_id: params.id },
          query: {
            include: undefined,
            ...(params.access !== undefined && {
              expose: { meta: { access: 'true' } },
            }),
          },
        },
        requestBody: buildRequestBody(params),
      })
    },
    onMutate,
    onSuccess,
  })
}
